import { MemberGroupFilter, MemberGroupsQuery, MemberGroupsQueryVariables } from '__generated__/graphql';
import { memberGroupsQuery } from 'components/domain/member-group/EmployeeGroupPicker/employee-group-picker-queries';
import { useActiveMember } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useActivePosition from 'hooks/models/position/useActivePosition';
import { useCallback } from 'react';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { getGraphQlEqualComparison } from 'utils/apolloUtils';

export type EmployeeGroupPickerItem = NonNullArrayField<MemberGroupsQuery['memberGroups']>;

export interface UseEmployeeGroupPickerDataOptions {
  filterByPermission?: boolean;
  excludeIds?: string[];
}

export function useEmployeeGroupPickerData({
  filterByPermission = false,
  excludeIds,
}: UseEmployeeGroupPickerDataOptions = {}) {
  const activeMember = useActiveMember();
  const activePosition = useActivePosition();
  const graphqlClient = useGraphQLClient();

  const getFilter = useCallback(
    (): MemberGroupFilter => {
      const filter: MemberGroupFilter = { deletedOn: { isNull: true } };

      if (filterByPermission && excludeIds) {
        throw new Error('Cannot use filterByPermission and excludeIds at the same time');
      }

      if (filterByPermission && activePosition?.scopeByGroup) {
        filter.id = getGraphQlEqualComparison(activeMember.memberGroupId) ?? null;
      } else if (excludeIds && excludeIds.length > 0) {
        filter.id = { doesNotContain: excludeIds };
      }

      return filter;
    },
    [filterByPermission, excludeIds, activePosition, activeMember]
  );

  const fetchPage = useCallback(
    async (after: string | null, first: number): Promise<EmployeeGroupPickerItem[]> => {
      const result = await graphqlClient.request<MemberGroupsQuery, MemberGroupsQueryVariables>({
        document: memberGroupsQuery,
        variables: {
          after,
          first,
          filter: getFilter(),
        },
      });

      return result.memberGroups ?? [];
    },
    [graphqlClient, getFilter]
  );

  const fetchSelected = useCallback(
    async (ids: string[]): Promise<EmployeeGroupPickerItem[]> => {
      const result = await graphqlClient.request<MemberGroupsQuery, MemberGroupsQueryVariables>({
        document: memberGroupsQuery,
        variables: { filter: { id: { contains: ids } } },
      });

      return result.memberGroups ?? [];
    },
    [graphqlClient]
  );

  return { fetchPage, fetchSelected };
}
